<template>
    <div class="function-management">
        <v-card class="edit-dialog" elevation="8">
            <v-card-title class="primary white--text">
                <v-icon left color="white">mdi-account-cog</v-icon>
                Gerenciamento de Permissões
                <v-spacer></v-spacer>
                <v-chip color="white" text-color="primary" v-if="usuario">
                    <v-avatar left>
                        <v-icon>mdi-account</v-icon>
                    </v-avatar>
                    {{ usuario.nomeUsuario }}
                </v-chip>
            </v-card-title>
            
            <v-divider></v-divider>
            
            <v-card-text>
                <v-form ref="form" v-model="valid" lazy-validation>
                    <v-container>
                        <v-row>
                            <v-col cols="12" md="8" lg="6">
                                <v-autocomplete 
                                    outlined 
                                    v-model="usuario" 
                                    item-value="pkUsuario"
                                    :rules="[v => !!v || 'Campo obrigatório']" 
                                    item-text="nomeUsuario"
                                    :items="usuarios" 
                                    label="Selecione um usuário" 
                                    persistent-hint
                                    prepend-icon="mdi-account-search"
                                    return-object 
                                    @change="usuarioSelected"
                                    clearable
                                    :loading="loadingUsers"
                                    placeholder="Digite para buscar usuários"
                                >
                                    <template v-slot:item="{ item }">
                                        <v-list-item-content>
                                            <v-list-item-title>{{ item.nomeUsuario }}</v-list-item-title>
                                            <v-list-item-subtitle>{{ item.autorizacao || 'Cargo não registrado' }}</v-list-item-subtitle>
                                            <v-list-item-subtitle>{{ item.username || 'Sem email registrado' }}</v-list-item-subtitle>
                                        </v-list-item-content>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>

                <v-expand-transition>
                    <div v-if="permissoesAll.length > 0">
                        <v-subheader class="pl-0">
                            <v-icon left>mdi-shield-account</v-icon>
                            Permissões do usuário
                            <v-chip small color="primary" class="ml-2" v-if="hasChanges">
                                <v-icon small left>mdi-alert-circle</v-icon>
                                Alterações não salvas
                            </v-chip>
                        </v-subheader>
                        
                        <v-list dense class="permissions-list">
                            <v-list-group 
                                v-for="(items, menuId) in groupedPermissions" 
                                :key="menuId" 
                                :value="false"
                                active-class="primary--text"
                            >
                                <template v-slot:activator>
                                    <v-list-item-icon>
                                        <v-icon>mdi-folder-account</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                        <v-list-item-title class="font-weight-medium">{{ items[0].menu }}</v-list-item-title>
                                        <v-list-item-subtitle>
                                            {{ items.filter(i => i.acesso === 1).length }} de {{ items.length }} permissões concedidas
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>
                                
                                <v-list-item 
                                    v-for="item in items" 
                                    :key="item.idFuncaoPed"
                                    class="permission-item"
                                >
                                    <v-list-item-action>
                                        <v-checkbox
                                            v-model="item.acesso"
                                            :true-value="1"
                                            :false-value="0"
                                            color="primary"
                                            @change="markAsChanged"
                                        ></v-checkbox>
                                    </v-list-item-action>
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.descricao }}</v-list-item-title>
                                        <v-list-item-subtitle v-if="item.detalhes">{{ item.detalhes }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                    <v-list-item-action>
                                        <v-chip small :color="item.acesso ? 'green' : 'grey'" dark>
                                            {{ item.acesso ? 'Ativo' : 'Inativo' }}
                                        </v-chip>
                                    </v-list-item-action>
                                </v-list-item>
                            </v-list-group>
                        </v-list>
                    </div>
                </v-expand-transition>

                <v-alert 
                    v-if="permissoesAll.length === 0 && usuario" 
                    border="left" 
                    colored-border 
                    type="info"
                    elevation="2"
                    class="mt-4"
                >
                    Nenhuma permissão encontrada para este usuário.
                </v-alert>
            </v-card-text>
            
            <v-card-actions v-if="usuario">
                <v-spacer></v-spacer>
                <v-btn 
                    color="primary" 
                    @click="savePermissions" 
                    :loading="saving"
                    :disabled="!hasChanges"
                >
                    <v-icon left>mdi-content-save</v-icon>
                    Salvar Alterações
                </v-btn>
            </v-card-actions>
        </v-card>
    </div>
</template>

<script>
import funcoesUsuarioService from '@/services/funcoesUsuarioService';
import funcaoUtilitarios from '@/utilitarios/funcaoUtilitarios'
import Swal from 'sweetalert2';

export default {
    data: () => ({
        valid: true,
        usuario: null,
        usuarios: [],
        permissoesAll: [],
        loadingUsers: false,
        saving: false,
        originalPermissions: [],
        hasChanges: false
    }),

    created() {
        this.carregarUsuarios();
    },

    computed: {
        i_id() {
            return require("local-storage").get('i_id');
        },
        nomeUsuario() {
            return require("local-storage").get('nomeUsuario');
        },
        groupedPermissions() {
            return this.permissoesAll.reduce((acc, perm) => {
                if (!acc[perm.idMenu]) {
                    acc[perm.idMenu] = [];
                }
                acc[perm.idMenu].push(perm);
                return acc;
            }, {});
        },
        nivel_user() {
            return require("local-storage").get('nivel_user');
        }
    },

    methods: {
        async carregarUsuarios() {
            this.loadingUsers = true;
            try {
                this.usuarios = await funcoesUsuarioService.getUsuarios(this.i_id);
            } catch (error) {
                Swal.fire('Informação', 'Erro ao carregar os usuários', 'error');
            } finally {
                this.loadingUsers = false;
            }
        },

        async usuarioSelected() {
            try {
                if (this.usuario) {
                    await this.loadUserPermissions();
                } else {
                    this.clearSelection();
                }
            } catch (error) {
                Swal.fire('Informação', 'Erro ao selecionar o usuário!', 'error');
            }
        },

        async loadUserPermissions() {
            this.permissoesAll = await funcoesUsuarioService.getAllFunctions(
                this.usuario.pkUsuario, 
                this.usuario.nivel
            );
            this.originalPermissions = JSON.parse(JSON.stringify(this.permissoesAll));
            this.hasChanges = false;
        },

        clearSelection() {
            this.usuario = null;
            this.permissoesAll = [];
            this.originalPermissions = [];
            this.hasChanges = false;
        },

        markAsChanged() {
            this.hasChanges = this.permissoesAll.some((perm, index) => {
                return perm.acesso !== this.originalPermissions[index].acesso;
            });
        },
        
        async savePermissions() {
            try {
                this.saving = true;

                // Prepare changes
                const changes = this.permissoesAll
                    .filter((perm, index) => perm.acesso !== this.originalPermissions[index].acesso)
                    .map(obj => ({
                        ...obj,
                        usuario: this.nomeUsuario,
                        instituicao: this.i_id,
                        fkUsuario: this.usuario.pkUsuario
                    }));

                // Save changes
                const response = await funcoesUsuarioService.updatePermissoes(changes);
                
                if (response == 200) {
                    await funcaoUtilitarios.init(true);
                    Swal.fire({
                        title: 'Sucesso',
                        text: 'Permissões Actualizadas com sucesso!',
                        icon: 'success',
                        timer: 1500,
                        showConfirmButton: false
                    });
                    
                    // Refresh permissions without changing the selected user
                    await this.loadUserPermissions();
                } else {
                    Swal.fire('Informação', 'Erro ao actualizar as permissões', 'error');
                }
            } catch (error) {
                Swal.fire('Informação', 'Erro ao realizar a operação', 'error');
            } finally {
                this.saving = false;
            }
        }
    }
};
</script>

<style scoped>
.function-management {
    padding: 24px;
    max-width: 1200px;
    margin: 0 auto;
}

.edit-dialog {
    border-radius: 12px;
    overflow: hidden;
}

.permissions-list {
    background: transparent;
    border: 1px solid #eee;
    border-radius: 8px;
}

.permission-item {
    transition: all 0.3s ease;
    border-left: 3px solid transparent;
}

.permission-item:hover {
    background-color: #f5f5f5;
    border-left: 3px solid var(--v-primary-base);
}

.v-subheader {
    font-size: 1rem;
    height: 48px;
    color: var(--v-primary-base);
}
</style>